import {
  ArrowLeft,
  Check,
  Heart,
  Lightbulb,
  Lock,
  Palette,
  Pencil,
  Plus,
  Tag as TagIcon,
  Trash2,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FormEvent, type MouseEvent } from "react";
import {
  COLOR_HEX,
  COLOR_HEX_DARK,
  COLOR_LABEL,
  PALETTE,
  type PaletteColor,
} from "@trip/domain/palette";
import { del, get, patch, post, type Asset, type Member, type Trip } from "../../lib/api.ts";
import { dayTabLabel } from "../../lib/days.ts";
import { useRefreshable } from "../../lib/refresh.ts";
import { clampNote, groupByDay, isLikes, type Tag } from "../../lib/tags.ts";
import { Link, navigate, tripPath, LIKES } from "../../lib/router.tsx";
import { useApi } from "../../lib/useApi.ts";
import { useThumbs } from "../../lib/thumbs.ts";
import {
  ActionMenu,
  ConfirmDialog,
  EmptyState,
  ICON,
  type MenuAction,
  PageState,
  RadioCard,
  reason,
  tagInk,
  useToast,
} from "../../ui/index.ts";
import { MediaGrid } from "./MediaGrid.tsx";
import { MediaPopup } from "./MediaPopup.tsx";

/**
 * The trip's media, filed.
 *
 * A tag is a label; filing several recordings under one is the grouping; and a tag carrying a
 * note is what the app calls an idea. So this is one wall of folders rather than three features
 * — a folder with a note on it reads as an instruction and a folder without one is just a
 * label, and both open the same way.
 *
 * `Likes` is the exception: it is not a tag but the recordings this reader has hearted, so it has
 * no id, cannot be renamed, repainted, noted, deleted or shared, and always comes first. It leads
 * the wall because it is the one folder on it guaranteed to be theirs, and the heart on a
 * recording — not this screen — is what fills and empties it.
 *
 * A folder is either the trip's or its owner's. A shared folder is what a tag has always been:
 * everyone sees it and anyone may file into it. A private one is on nobody else's wall, so the
 * cards say which is which — a lock on the private ones, and on a shared one somebody else made,
 * their name. Nothing marks a shared folder of your own: that is the ordinary case, and a wall
 * where every card explains itself is a wall nobody reads.
 *
 * Two different rights, which this screen keeps apart. `canEdit` is the name, the note, the
 * colour and the delete — the owner's, or an admin's on a folder the trip shares. `mine` is the
 * audience, which is the owner's alone: taking somebody else's folder private would hand it to
 * them and hide it from whoever did it. Both arrive decided by the server, because a client that
 * works them out from an owner id and a role list offers buttons the server then refuses.
 *
 * Every real folder wears one of the trip's eight colours. A wall of grey cards can only be told
 * apart by reading it, and a name is slower than a colour; so the hue is the first thing that
 * says which folder this is, and the name confirms it. The server picks one when the tag is made
 * — whichever the trip is using least — so nobody has to choose before they can file anything,
 * and the owner can repaint it afterwards from inside the folder.
 */

/** The reader's own zone decides which day a recording belongs to. */
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
/** How much of a note a folder card shows before the rest waits inside. */
const CARD_NOTE = 120;

/** What the folder holds, in words: a bare numeral under a name reads as a riddle. */
const held = (n: number) => `${n} ${n === 1 ? "recording" : "recordings"}`;

/**
 * A click the host would rather have than the link's own navigation.
 *
 * Only the plain left one: a folder is a real page whoever is showing it, so a modifier or the
 * middle button still means open it as a page, and a host that keeps the folder itself has no
 * business taking that away.
 */
const insteadOfFollowing = (go: () => void) => (e: MouseEvent<HTMLAnchorElement>) => {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  go();
};

export function TagsTab({
  trip,
  itemId,
  onFolder,
  onOpen,
}: {
  trip: Trip;
  /** The folder being looked at, or null for the wall of them. */
  itemId: string | null;
  /**
   * What opening a folder — and leaving it — means, when the host keeps the open one itself.
   * By default it is the address bar: a folder is a page of its own that can be linked and come
   * back to. A host showing the library inside a page of its own passes this instead. On the
   * editor's map the library is one panel beside a map and a clock, and following the link there
   * took the whole screen away from them to show a folder on its own.
   */
  onFolder?: (tagId: string | null) => void;
  /**
   * Where a tapped recording is read, when the host has somewhere of its own — the same offer
   * the grid in the other half of the library takes, and for the same reason: the pager inside a
   * folder takes the whole screen, which on the editor's map is the map.
   */
  onOpen?: (asset: Asset) => void;
}) {
  const tags = useApi<{ items: Tag[] }>(`/v1/trips/${trip.id}/tags`);
  const open = (tags.data?.items ?? []).find((t) => t.id === itemId) ?? null;

  return itemId === null ? (
    <TagWall trip={trip} tags={tags} {...(onFolder ? { onFolder } : {})} />
  ) : (
    <TagFolder
      trip={trip}
      tag={open}
      tagId={itemId}
      onChanged={() => void tags.reload()}
      {...(onFolder ? { onLeave: () => onFolder(null) } : {})}
      {...(onOpen ? { onOpen } : {})}
    />
  );
}

// ------------------------------------------------------------------------------- the wall

function TagWall({
  trip,
  tags,
  onFolder,
}: {
  trip: Trip;
  tags: ReturnType<typeof useApi<{ items: Tag[] }>>;
  /** Where opening a folder is handled, when it is not the address bar. */
  onFolder?: (tagId: string) => void;
}) {
  const [making, setMaking] = useState(false);
  const items = tags.data?.items ?? [];
  // One batch for the whole wall, not one grant per folder: thirty folders at four thumbnails
  // each is 120 images, and the cache is shared with the grid.
  const previewIds = useMemo(
    () => [...new Set(items.flatMap((t) => t.previewAssetIds))],
    [tags.data],
  );
  const thumbs = useThumbs(trip.id, previewIds);

  return (
    <div className="stack">
      <div className="row wrap">
        <button type="button" className="button primary" onClick={() => setMaking(true)}>
          <Plus {...ICON} aria-hidden="true" />
          New tag
        </button>
      </div>

      {making && (
        <NewTag
          trip={trip}
          onClose={() => setMaking(false)}
          onMade={() => {
            setMaking(false);
            void tags.reload();
          }}
        />
      )}

      <PageState
        data={tags.data}
        error={tags.error}
        loading={tags.loading}
        reload={tags.reload}
        // Likes is always there and is not a tag, so a trip whose only folder is Likes still
        // needs telling what a tag is for.
        isEmpty={(d) => d.items.filter((t) => !isLikes(t)).length === 0}
        empty={
          <EmptyState
            title="No tags yet"
            body="A tag is a label — “golden hour”, “the market”. Filing recordings under the same one is how they are grouped, and a tag with a note on it is an idea."
          />
        }
      >
        {(d) => (
          <ul className="tag-wall plain">
            {d.items.map((t) => (
              <li key={t.id ?? LIKES}>
                <Link
                  className="tag-folder"
                  to={tripPath(trip.id, "tags", t.id ?? LIKES)}
                  {...(onFolder
                    ? { onClick: insteadOfFollowing(() => onFolder(t.id ?? LIKES)) }
                    : {})}
                  data-idea={t.note !== null}
                  data-colored={t.color !== null}
                  data-private={t.visibility === "PRIVATE"}
                  style={tagInk(t.color)}
                >
                  {t.note && (
                    <span className="tag-folder-note">
                      <Lightbulb size={14} strokeWidth={2} aria-hidden="true" />
                      {clampNote(t.note, CARD_NOTE)}
                    </span>
                  )}
                  <span className="tag-folder-previews" aria-hidden="true">
                    {t.previewAssetIds
                      .slice(0, 4)
                      .map((id) =>
                        thumbs[id] ? (
                          <img key={id} src={thumbs[id]} alt="" />
                        ) : (
                          <span key={id} className="tag-folder-blank" />
                        ),
                      )}
                  </span>
                  <span className="tag-folder-foot">
                    <span className="tag-folder-name">
                      {/* The colour as a mark beside the name rather than as the name's ink:
                          a folder called "golden hour" written in yellow is a colour you have
                          to read the text to see, and three of those are a ransom note. */}
                      {t.color !== null && <span className="tag-dot" aria-hidden="true" />}
                      {isLikes(t) && <Heart size={14} strokeWidth={2} aria-hidden="true" />}
                      {t.name}
                    </span>
                    <span className="tag-folder-count">{t.count}</span>
                  </span>
                  {/* Who else can see this, said only when it is not the ordinary case. A lock
                      on what nobody else sees; on a shared folder somebody else made, their
                      name, because "whose is this" and "can anyone see it" are two questions
                      and one mark cannot answer both. A shared folder of your own says
                      nothing — a wall where every card explains itself is unreadable. */}
                  {t.visibility === "PRIVATE" ? (
                    <span className="tag-folder-who">
                      <Lock size={12} strokeWidth={2} aria-hidden="true" />
                      Only you
                    </span>
                  ) : (
                    !t.mine &&
                    t.ownerName !== null && (
                      <span className="tag-folder-who">
                        <Users size={12} strokeWidth={2} aria-hidden="true" />
                        {t.ownerName}
                      </span>
                    )
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </PageState>
    </div>
  );
}

/**
 * The trip's eight, to pick one of.
 *
 * Borrows the member picker's swatches wholesale, because they are the same eight colours and a
 * second set of controls for choosing between them would be a second thing to keep accessible.
 * The one difference is that none of them is ever taken: a member's colour has to be theirs
 * alone to answer "whose line is that?", while two folders in the same green are still told
 * apart by their names — and a trip is allowed more tags than the palette has colours.
 */
function ColorPool({
  value,
  label,
  onPick,
}: {
  /** The colour now, or null while the server is still the one choosing. */
  value: PaletteColor | null;
  label: string;
  onPick: (c: PaletteColor) => void;
}) {
  return (
    <fieldset className="color-pool">
      <legend className="label">{label}</legend>
      {PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          className="pool-swatch"
          // From the one palette, never a second copy of it in CSS. Both themes go down
          // together because CSS, not this component, knows which one is on.
          style={
            { "--swatch": COLOR_HEX[c], "--swatch-dark": COLOR_HEX_DARK[c] } as React.CSSProperties
          }
          data-on={value === c}
          aria-pressed={value === c}
          // Said in words, because a colour is the one thing a swatch cannot say to somebody
          // who cannot see it.
          aria-label={COLOR_LABEL[c]}
          title={COLOR_LABEL[c]}
          onClick={() => onPick(c)}
        >
          {value === c && <Check size={16} strokeWidth={3} aria-hidden="true" />}
        </button>
      ))}
    </fieldset>
  );
}

/**
 * Who a new folder is for.
 *
 * Said as two choices rather than one switch, because "private" on its own does not tell you
 * what the other state is, and the pair is the whole decision: the trip's, or yours. Real radios
 * in cards, the same control the app uses wherever two options have to be read before one is
 * picked.
 */
function VisibilityChoice({
  value,
  onPick,
}: {
  value: "PUBLIC" | "PRIVATE";
  onPick: (v: "PUBLIC" | "PRIVATE") => void;
}) {
  return (
    <fieldset className="stack">
      <legend className="label">Who can see it</legend>
      <RadioCard
        name="tag-visibility"
        value="PUBLIC"
        checked={value === "PUBLIC"}
        label="Everyone on the trip"
        description="They can open it and file their own recordings into it."
        onChange={() => onPick("PUBLIC")}
      />
      <RadioCard
        name="tag-visibility"
        value="PRIVATE"
        checked={value === "PRIVATE"}
        label="Only you"
        description="Nobody else sees the folder, or what you put in it."
        onChange={() => onPick("PRIVATE")}
      />
    </fieldset>
  );
}

function NewTag({
  trip,
  onClose,
  onMade,
}: {
  trip: Trip;
  onClose: () => void;
  onMade: () => void;
}) {
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  // Null until somebody picks: the server hands out whichever colour the trip is using least,
  // which is a better answer than any default this form could show, and it means nobody has to
  // make a decision about paint before they can make a folder.
  const [color, setColor] = useState<PaletteColor | null>(null);
  // Shared is what a tag has always been, so it is the default — and it is sent only when it is
  // not, so the server's default says it in one place rather than two.
  const [visibility, setVisibility] = useState<"PUBLIC" | "PRIVATE">("PUBLIC");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    post(`/v1/trips/${trip.id}/tags`, {
      name: name.trim(),
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(color ? { color } : {}),
      ...(visibility === "PRIVATE" ? { visibility } : {}),
    })
      .then(onMade)
      .catch((err: Error) => setError(reason(err)))
      .finally(() => setBusy(false));
  }

  return (
    <form className="card stack" onSubmit={submit} aria-label="New tag">
      <label className="field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
      </label>
      <label className="field">
        <span>
          Note <span className="hint">(optional — a tag with one is an idea)</span>
        </span>
        {/* A textarea, because an idea is a sentence or three and a one-line field makes a
            four-thousand-character note unwritable as well as unreadable. */}
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={4000}
          rows={3}
        />
      </label>
      <ColorPool
        value={color}
        label="Colour (optional)"
        onPick={(c) => setColor(c === color ? null : c)}
      />
      <VisibilityChoice value={visibility} onPick={setVisibility} />
      <div className="row">
        <button className="button primary" disabled={busy || !name.trim()}>
          Create
        </button>
        <button type="button" className="button" onClick={onClose}>
          Cancel
        </button>
      </div>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </form>
  );
}

// ----------------------------------------------------------------------------- one folder

export function TagFolder({
  trip,
  tag: fromWall,
  tagId,
  onChanged,
  onLeave,
  onOpen,
}: {
  trip: Trip;
  tag: Tag | null;
  tagId: string;
  onChanged: () => void;
  /** The way back to the wall, when the host keeps the open folder rather than the address bar. */
  onLeave?: () => void;
  /** Where a tapped recording is read, when the host would rather not give up the screen. */
  onOpen?: (asset: Asset) => void;
}) {
  const likes = tagId === LIKES;
  const members = useApi<{ items: Member[] }>(`/v1/trips/${trip.id}/members`);
  // Its own read, not the wall's row. Opening a folder by link does not mean the wall has
  // loaded, and a delete dialog promising "untags 0 recordings" while the server untags twelve
  // is the opposite of saying what will happen first.
  const own = useApi<Tag>(likes ? null : `/v1/trips/${trip.id}/tags/${tagId}`);
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<Asset | null>(null);
  const [editing, setEditing] = useState<"name" | "note" | null>(null);
  // Its own flag rather than a third `editing` mode: the swatches are not a field with a Save
  // button, a tap on one is the whole act.
  const [painting, setPainting] = useState(false);
  const [asking, setAsking] = useState(false);
  // Taking a folder private is confirmed; sharing one is not. Going private takes something
  // away from people who filed into it, and they are not here to be asked.
  const [sealing, setSealing] = useState(false);
  const toast = useToast();

  /**
   * Everything in the folder, read to the end.
   *
   * Both halves of this matter. The filter is the server's — `liked=1` for the folder with no
   * id — because sifting a page client-side means a trip whose first page is entirely
   * unhearted shows an empty Likes folder under a header counting dozens. And it pages: the
   * server answers in capture order, oldest first, so one page of a folder of four hundred is
   * the four hundred oldest, and the grid would show none of the recordings the card just
   * previewed. A folder is bounded, like a day in the Media tab, so it is read to the end.
   */
  const load = useCallback(async () => {
    const filter = likes ? "liked=1" : `tagId=${tagId}`;
    const all: Asset[] = [];
    let cursor: string | undefined;
    do {
      const q = `limit=200&${filter}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`;
      const page = await get<{ items: Asset[]; nextCursor: string | null }>(
        `/v1/trips/${trip.id}/assets?${q}`,
      );
      all.push(...page.items);
      cursor = page.nextCursor ?? undefined;
    } while (cursor);
    setAssets(all);
  }, [trip.id, tagId, likes]);

  useEffect(() => {
    setAssets(null);
    load().catch((e: Error) => setError(reason(e)));
  }, [load]);
  useRefreshable(load);

  // The folder's own read wins; the wall's row is the fallback while it is in flight.
  const tag = own.data ?? fromWall;
  const groups = useMemo(() => groupByDay(assets ?? [], TIME_ZONE), [assets]);
  const thumbs = useThumbs(
    trip.id,
    (assets ?? []).filter((a) => a.thumbReady).map((a) => a.id),
  );
  const name = tag?.name ?? (likes ? "Likes" : "…");

  /**
   * What this reader may do to this folder, in the order the menu shows it.
   *
   * Built from the same two rights the wall reads, and they are not interchangeable: `canEdit`
   * is the name, the note, the colour and the delete — the owner's, or an admin's on a folder
   * the trip shares. `mine` is the audience, which is the owner's alone. So an admin tidying
   * somebody else's shared folder gets four items and no fifth, and an item that would answer
   * 403 is never offered in the first place.
   *
   * Delete comes last and marks itself destructive: it untags everybody's recordings.
   */
  const actions: MenuAction[] = [];
  if (!likes && tag?.canEdit) {
    actions.push(
      {
        label: "Rename",
        icon: <Pencil {...ICON} aria-hidden="true" />,
        onPick: () => setEditing("name"),
      },
      {
        label: tag.note ? "Edit note" : "Add a note",
        // Only worth explaining when there is no note yet: once there is one, the folder is
        // already an idea and the card above says so.
        ...(tag.note ? {} : { detail: "a folder with a note on it is an idea" }),
        icon: <Lightbulb {...ICON} aria-hidden="true" />,
        onPick: () => setEditing("note"),
      },
      {
        label: "Colour",
        icon: <Palette {...ICON} aria-hidden="true" />,
        onPick: () => setPainting(true),
      },
    );
  }
  if (!likes && tag?.mine) {
    actions.push(
      tag.visibility === "PUBLIC"
        ? {
            label: "Only you",
            detail: "stop the trip seeing this folder",
            icon: <Lock {...ICON} aria-hidden="true" />,
            onPick: () => setSealing(true),
          }
        : {
            label: "Share with the trip",
            detail: "everyone can open it and file into it",
            icon: <Users {...ICON} aria-hidden="true" />,
            onPick: () => void save({ visibility: "PUBLIC" }),
          },
    );
  }
  if (!likes && tag?.canEdit) {
    actions.push({
      label: "Delete",
      detail: "the recordings are kept",
      icon: <Trash2 {...ICON} aria-hidden="true" />,
      destructive: true,
      onPick: () => setAsking(true),
    });
  }

  async function save(body: {
    name?: string;
    note?: string | null;
    color?: PaletteColor;
    visibility?: "PUBLIC" | "PRIVATE";
  }) {
    setError(null);
    try {
      await patch(`/v1/trips/${trip.id}/tags/${tagId}`, body);
      setEditing(null);
      // Both reads, because both are showing the tag. The wall's row is what the header falls
      // back to, but the folder's own read is what it prefers — so reloading only the wall left
      // a rename, a note or a repaint invisible until the page was pulled.
      onChanged();
      void own.reload();
    } catch (e) {
      setError(reason(e as Error));
    }
  }

  async function remove() {
    setAsking(false);
    try {
      await del(`/v1/trips/${trip.id}/tags/${tagId}`);
      toast(`Deleted “${name}”. The recordings are kept.`);
      onChanged();
      // Not history.back(): a folder reached by a shared link has no app page behind it, and
      // going back would leave the app entirely. Where the host keeps the open folder there is
      // no address to go back to either — it closes the folder instead.
      if (onLeave) onLeave();
      else navigate(tripPath(trip.id, "tags"));
    } catch (e) {
      setError(reason(e as Error));
    }
  }

  return (
    <div className="stack">
      {/* The folder stands where the tabs were: its name centred under the icon that says
          whether it is a label or an idea, how much it holds spelled out beneath, and the
          same underline the tabs drew. The way out is a chevron, kept to one side of it. */}
      <div className="tag-head" data-colored={tag?.color != null} style={tagInk(tag?.color)}>
        <Link
          className="tag-head-back"
          to={tripPath(trip.id, "tags")}
          {...(onLeave ? { onClick: insteadOfFollowing(onLeave) } : {})}
          aria-label="All tags"
        >
          <ArrowLeft {...ICON} aria-hidden="true" />
        </Link>
        <h2 className="tag-head-name">
          {/* The icon carries the folder's colour, and the underline beneath takes it too. The
              name itself stays in the page's ink: a heading is for reading. */}
          {tag?.note ? (
            <Lightbulb {...ICON} aria-hidden="true" />
          ) : (
            <TagIcon {...ICON} aria-hidden="true" />
          )}
          {name}
        </h2>
        <p className="tag-head-count">{held(tag?.count ?? assets?.length ?? 0)}</p>
        {/* Everything this reader may do to the folder, behind one target at the edge of the
            header, mirroring the way out at the other edge. Five buttons in a row under the
            header could not be aimed at on a phone, and the audience control made it six.
            Nothing here when there is nothing to offer — an empty menu is worse than no menu,
            and Likes has nothing, because the heart is what fills it. */}
        {actions.length > 0 && (
          <ActionMenu label={`Folder options for “${name}”`} actions={actions} />
        )}
      </div>

      {/* The whole note here, however long: the card clamped it, this is where it is read. */}
      {tag?.note && <p className="tag-head-note">{tag.note}</p>}

      {painting && (
        <div className="card">
          <ColorPool
            value={tag?.color ?? null}
            label={`Colour for “${name}”`}
            onPick={(c) => {
              // Closed on the way out, not on the way back: the folder it repaints is the one
              // the person is looking at, so the change is its own confirmation.
              setPainting(false);
              void save({ color: c });
            }}
          />
        </div>
      )}

      {editing && (
        <EditField
          kind={editing}
          initial={(editing === "name" ? tag?.name : tag?.note) ?? ""}
          onCancel={() => setEditing(null)}
          onSave={(value) =>
            save(editing === "name" ? { name: value } : { note: value.trim() || null })
          }
        />
      )}

      {/* What it costs, named and counted, before it happens. The recordings other members
          filed stay filed and stay yours to see — they just stop being visible to the people
          who put them there, and those people are not here to be asked. A folder nobody else
          filed into says nothing about a count it has not got. */}
      <ConfirmDialog
        open={sealing}
        title={`Make “${name}” private?`}
        body={
          (tag?.othersFiled ?? 0) > 0
            ? `Only you will see this folder. ${held(tag!.othersFiled)} other people filed here will stop being visible to them. Nothing is deleted.`
            : "Only you will see this folder, and what you put in it."
        }
        confirmLabel="Make it private"
        onConfirm={() => {
          setSealing(false);
          void save({ visibility: "PRIVATE" });
        }}
        onCancel={() => setSealing(false)}
      />

      <ConfirmDialog
        open={asking}
        title={`Delete “${name}”?`}
        body={`This untags ${tag?.count ?? 0} ${tag?.count === 1 ? "recording" : "recordings"}. The recordings themselves are kept.`}
        confirmLabel="Delete tag"
        destructive
        onConfirm={() => void remove()}
        onCancel={() => setAsking(false)}
      />

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {assets === null ? (
        <p className="muted">Loading…</p>
      ) : groups.length === 0 ? (
        likes ? (
          <EmptyState
            title="Nothing liked yet"
            body="Tap the heart on a recording and it will be waiting here. Only you see this folder; the heart itself is the trip's to count."
          />
        ) : (
          <EmptyState
            title="Nothing is tagged with this yet"
            body="Select recordings in the Media tab and tag them, and they will appear here."
          />
        )
      ) : (
        groups.map((g) => (
          <section key={g.day ?? "undated"} className="stack">
            <h3 className="tag-day">{g.day === null ? "No time" : dayTabLabel(g.day)}</h3>
            <MediaGrid
              assets={g.assets}
              thumbs={thumbs}
              selected={new Set()}
              selecting={false}
              onOpen={onOpen ?? setOpened}
              onToggle={() => undefined}
            />
          </section>
        ))
      )}

      {opened && (
        <MediaPopup
          trip={trip}
          // Inside a folder the collection is the folder: paging moves through what is filed
          // under this tag, in the capture order the grid shows, and stops at its edges. The
          // whole folder is already read, so there is nothing to fetch at either end.
          items={assets ?? []}
          startId={opened.id}
          members={members.data?.items ?? []}
          onActive={setOpened}
          onClose={() => setOpened(null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function EditField({
  kind,
  initial,
  onCancel,
  onSave,
}: {
  kind: "name" | "note";
  initial: string;
  onCancel: () => void;
  onSave: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <form
      className="card row wrap"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(value);
      }}
      aria-label={kind === "name" ? "Rename tag" : "Edit note"}
    >
      <label className="field grow">
        <span>{kind === "name" ? "New name" : "Note"}</span>
        {kind === "name" ? (
          <input value={value} onChange={(e) => setValue(e.target.value)} maxLength={40} />
        ) : (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={4000}
            rows={3}
          />
        )}
      </label>
      <button className="button primary">Save</button>
      <button type="button" className="button" onClick={onCancel}>
        Cancel
      </button>
    </form>
  );
}
